import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

export type Currency = 'USD' | 'EUR';

export interface CurrencyInfo {
  code: Currency;
  symbol: string;
  locale: string;
  apiKey: string; // Key used in CoinGecko API (usd, eur)
}

export const CURRENCIES: Record<Currency, CurrencyInfo> = {
  USD: { code: 'USD', symbol: '$', locale: 'en-US', apiKey: 'usd' },
  EUR: { code: 'EUR', symbol: '€', locale: 'fr-FR', apiKey: 'eur' }
};

interface CurrencyContextType {
  currency: Currency;
  currencyInfo: CurrencyInfo;
  setCurrency: (currency: Currency) => void;
  formatValue: (value: number, decimals?: number) => string;
}

const CurrencyContext = createContext<CurrencyContextType>({
  currency: 'USD',
  currencyInfo: CURRENCIES.USD,
  setCurrency: () => {},
  formatValue: (value) => `$${value.toFixed(2)}`
});

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const [currency, setCurrencyState] = useState<Currency>(() => {
    const stored = localStorage.getItem('preferred_currency');
    if (stored === 'EUR' || stored === 'USD') {
      return stored;
    }
    return 'USD';
  });

  useEffect(() => {
    localStorage.setItem('preferred_currency', currency);
    // Dispatch event for components that don't use context
    window.dispatchEvent(new CustomEvent('currency-change', { detail: currency }));
  }, [currency]);

  // Listen for currency-change events (from backup restore)
  useEffect(() => {
    const handleCurrencyChange = (event: CustomEvent<Currency>) => {
      setCurrencyState(event.detail);
    };

    window.addEventListener('currency-change', handleCurrencyChange as EventListener);
    return () => {
      window.removeEventListener('currency-change', handleCurrencyChange as EventListener);
    };
  }, []);

  const setCurrency = useCallback((newCurrency: Currency) => {
    console.log('💱 CurrencyContext: Changing currency to', newCurrency);
    setCurrencyState(newCurrency);
  }, []);

  const currencyInfo = CURRENCIES[currency];

  const formatValue = useCallback((value: number, decimals: number = 2): string => {
    const formatted = value.toLocaleString(currencyInfo.locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });

    // EUR uses symbol after the value, USD uses symbol before
    if (currency === 'EUR') {
      return `${formatted} ${currencyInfo.symbol}`;
    }
    return `${formatted} ${currencyInfo.symbol}`;
  }, [currency, currencyInfo]);

  return (
    <CurrencyContext.Provider value={{ currency, currencyInfo, setCurrency, formatValue }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrencyContext() {
  return useContext(CurrencyContext);
}
